import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { createPalletExchange } from '../../lib/api';

/**
 * Truck-dock pallet exchange. Driver brings X, takes Y back.
 * Persisted as a paired (in + out) movement via /api/pallets/exchanges.
 */
export default function ExchangeDialog({ open, setOpen, types, warehouses, merchants, onSaved }) {
  const [form, setForm] = useState({
    type_id: '', received_qty: 0, returned_qty: 0,
    warehouse_id: '', merchant_id: '',
    driver_name: '', truck_rego: '', docket_number: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({
    type_id: '', received_qty: 0, returned_qty: 0,
    warehouse_id: '', merchant_id: '',
    driver_name: '', truck_rego: '', docket_number: '', notes: '',
  });

  const submit = async () => {
    if (!form.type_id) return toast.error('Pick a pallet type');
    const inQty = Number(form.received_qty || 0);
    const outQty = Number(form.returned_qty || 0);
    if (inQty < 0 || outQty < 0) return toast.error('Quantities cannot be negative');
    if (inQty === 0 && outQty === 0) return toast.error('At least one side must be > 0');
    setSaving(true);
    try {
      const payload = {
        type_id: form.type_id,
        received_qty: inQty, returned_qty: outQty,
      };
      ['warehouse_id', 'merchant_id', 'driver_name', 'truck_rego', 'docket_number', 'notes']
        .forEach((k) => { if (form[k] && String(form[k]).trim()) payload[k] = form[k]; });
      await createPalletExchange(payload);
      toast.success(`Exchange posted · net ${inQty - outQty} pallets`);
      setOpen(false);
      reset();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="pallet-exchange-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowsClockwise size={18} /> Truck pallet exchange</DialogTitle>
          <DialogDescription>
            Driver brings <em>received</em> pallets and leaves with <em>returned</em>.
            Persisted as a linked in/out pair so the audit trail keeps the swap intact.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Pallet type *</Label>
            <select className="w-full border border-border bg-background h-9 px-2 text-sm"
              value={form.type_id} onChange={(e) => setForm({ ...form, type_id: e.target.value })}
              data-testid="exchange-form-type">
              <option value="">Select…</option>
              {types.filter((t) => t.is_active).map((t) =>
                <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Received qty (driver brings IN)</Label>
              <Input type="number" min="0" value={form.received_qty}
                onChange={(e) => setForm({ ...form, received_qty: e.target.value })}
                data-testid="exchange-form-received" />
            </div>
            <div>
              <Label>Returned qty (driver takes OUT)</Label>
              <Input type="number" min="0" value={form.returned_qty}
                onChange={(e) => setForm({ ...form, returned_qty: e.target.value })}
                data-testid="exchange-form-returned" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Warehouse</Label>
              <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                data-testid="exchange-form-warehouse">
                <option value="">—</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Merchant (optional)</Label>
              <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                data-testid="exchange-form-merchant">
                <option value="">—</option>
                {merchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Driver</Label>
              <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
                data-testid="exchange-form-driver" />
            </div>
            <div>
              <Label>Truck rego</Label>
              <Input value={form.truck_rego} onChange={(e) => setForm({ ...form, truck_rego: e.target.value })}
                data-testid="exchange-form-rego" />
            </div>
            <div>
              <Label>Docket no.</Label>
              <Input value={form.docket_number} onChange={(e) => setForm({ ...form, docket_number: e.target.value })}
                data-testid="exchange-form-docket" />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full border border-border bg-background p-2 text-sm" rows={2}
              data-testid="exchange-form-notes" />
          </div>
          <div className="bg-muted/30 border border-border p-2 text-xs text-muted-foreground" data-testid="exchange-form-summary">
            Net change: <strong className="font-mono">{(Number(form.received_qty || 0) - Number(form.returned_qty || 0))}</strong> pallets to your stock.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="exchange-form-submit">
            {saving ? 'Posting…' : 'Post exchange'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
